// One typed report table — the shape every report shares (columns with a
// columnType, rows keyed by column, an optional totals row). Money renders
// in dollars from cents, percent from basis points. Reusable by any scope's
// report page (store, restaurant, platform).

import { dateTimeOf, money, type ReportColumn } from '../../lib/api';

type Cell = string | number | null | undefined;
type Row = Record<string, Cell>;

/** A cell as the screen shows it: money in dollars, percent from basis points. */
export function show(v: Cell, c: ReportColumn, tz?: string): string {
  if (v === null || v === undefined || v === '') return '—';
  switch (c.columnType) {
    case 'money':
      return money(Number(v));
    case 'percent':
      return `${(Number(v) / 100).toFixed(2)}%`;
    case 'integer':
      return Number(v).toLocaleString('en-AU');
    case 'minutes':
      return `${Number(v).toLocaleString('en-AU')} min`;
    case 'decimal':
      return Number(v).toLocaleString('en-AU', { maximumFractionDigits: 3 });
    case 'datetime':
      return dateTimeOf(String(v), tz);
    default:
      return String(v);
  }
}

export const numeric = (c: ReportColumn) => !['text', 'date', 'datetime'].includes(c.columnType);

export function ReportTable(props: { columns: ReportColumn[]; rows: Row[]; totals: Row | null; tz?: string; section: string }) {
  const { columns, rows, totals, tz } = props;
  return (
    <table className="tbl" data-section={props.section}>
      <thead>
        <tr>{columns.map((c) => <th key={c.key} className={numeric(c) ? 'num' : undefined} data-col={c.key} data-type={c.columnType}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr style={{ cursor: 'default' }}><td colSpan={columns.length} style={{ color: 'var(--ink-3)' }}>Nothing in this period.</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={i} style={{ cursor: 'default' }} data-row={i}>
            {columns.map((c) => (
              <td key={c.key} className={numeric(c) ? 'num' : undefined} data-col={c.key} data-value={r[c.key] ?? ''} style={numeric(c) ? { fontVariantNumeric: 'tabular-nums' } : undefined}>
                {show(r[c.key], c, tz)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {totals && (
        <tfoot>
          <tr data-totals style={{ cursor: 'default', fontWeight: 700, borderTop: '2px solid var(--ink)' }}>
            {columns.map((c, i) => (
              <td key={c.key} className={numeric(c) ? 'num' : undefined} data-col={c.key} data-value={totals[c.key] ?? ''} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {c.key in totals ? show(totals[c.key], c, tz) : i === 0 ? 'Total' : ''}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  );
}
